import type React from "react"
import { JournalTime } from "./journal-time"

/**
 * Shared one-line layout for every journal feed row: a centered leading icon,
 * the event headline with the actor inline (muted, `·`-separated), and the
 * relative time pushed to the right. Compact on a mobile browser, roomier from
 * `sm:` up. The row's `data-event-type` lives here (its parent JournalRow
 * wrapper carries `data-event-id` / `data-source-marker`); the divider lives on
 * that wrapper (`last:border-b-0`), not here.
 */
export function JournalRowShell({
	icon,
	headline,
	actor,
	iso,
	eventType,
	aria,
}: {
	icon: React.ReactNode
	headline: React.ReactNode
	/** Muted, inline after the headline. Omitted for actor-less rows (unknown). */
	actor?: string
	iso: string
	eventType: string
	aria: string
}) {
	return (
		<div
			className="flex items-center gap-2.5 py-2 sm:gap-3 sm:py-2.5"
			data-event-type={eventType}
		>
			<span className="shrink-0 text-muted-foreground">{icon}</span>
			<p className="min-w-0 flex-1 truncate text-sm sm:text-base">
				<span className="font-medium text-foreground">{headline}</span>
				{actor ? (
					<span className="text-muted-foreground"> · {actor}</span>
				) : null}
			</p>
			<span className="shrink-0 whitespace-nowrap">
				<JournalTime iso={iso} />
			</span>
			<span className="sr-only">{aria}</span>
		</div>
	)
}
